import React, { useState, useEffect } from 'react';
import { Row, Col, Input, Button, Table, Modal, Select, Checkbox, Spin, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import Uploader from '../../components/imgUploader';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import { categoryListTreeAdmin, categoryDelete } from '../../services/categoryApi';
import { post } from '../../services/fetch';
import { useAuth } from '../../hooks/auth';

interface DataType {
  key: string;
  name: string;
  popularity: number;
  isPublished: boolean;
}

const Categories: React.FC = () => {
  const { user } = useAuth();
  
  useEffect(()=>{getData()}, [])
  const getData = () =>  {
    setIsLoading(true);
    categoryListTreeAdmin().then(data=>{
      setIsLoading(false);
      setData(data);
    })
  }

  const categTemplate = {title: '', id: null};
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteCategoryId, setDeleteCategoryId] = useState<number | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [currentCategory, setCurrentCategory] = useState(categTemplate);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<any>([]);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  const handleEdit = (categ: any) => {
    setCurrentCategory({title: categ.name, id: categ.id})
    setFileList([{
      uid: '-1',
      name: categ.imgSource,
      status: 'done',
      url: '/api/public/categories/'+categ.imgSource,
    },])
    setIsModalOpen(true);
  }

  const handleDelete = (id: number) => {
    setDeleteCategoryId(id);
    setIsDeleteModalOpen(true);
  }

  const confirmDelete = async () => {
    if (!deleteCategoryId) return;
    
    let result = {status: ''};
    result = await categoryDelete(deleteCategoryId);
    if(result.status === 'has children') {
      message.error('Нельзя удалять категорию, которая содержит подкатегории');
    } else if(result.status === 'success') {
      message.success('Категория успешно удалена');
      getData();
    }
    setIsDeleteModalOpen(false);
    setDeleteCategoryId(null);
  }

  const columns: ColumnsType<DataType> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <div>{text}</div>,
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: '',
      width: '75px',
      dataIndex: 'id',
      key: 'id',
      render: (id, record) => <div>
        {user.type === 'admin' && <span onClick={()=>handleDelete(id)} style={{color: '#A40207', cursor: 'pointer'}}><DeleteOutlined style={{ fontSize: 18 }} /></span>}
        <span onClick={()=>handleEdit(record)} style={{color: '#A40207', cursor: 'pointer'}}><EditOutlined style={{ float: 'right', fontSize: 18 }} /></span>
        </div>
    }
  ];

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentCategory(categTemplate);
    setValidationErrors({});
  }

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!currentCategory.title?.trim()) {
      errors.title = 'Название категории обязательно для заполнения';
    }
    
    if (fileList.length === 0) {
      errors.image = 'Изображение обязательно для загрузки';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true)
    const formData = new FormData();
    formData.append('file', fileList[0] as RcFile);
    formData.append('title', currentCategory.title);
    if(currentCategory.id) formData.append('id', currentCategory.id);
    post('/category/update', formData, false)
      .then(() => {
        message.success(currentCategory.id ? 'Категория успешно обновлена' : 'Категория успешно создана');
        setFileList([]);
        setIsLoading(false);
        closeModal();
        getData();
      })
      .catch(() => {
        message.error('Ошибка при сохранении категории');
        setIsLoading(false);
      });
  };

  const getModalTitle = () => {
    return currentCategory.id ? 'Редактирование категории' : 'Добавление новой категории';
  };

  return (
    <Spin spinning={isLoading}>
      <Row gutter={[16, 16]} style={{ marginTop: 40 }} >
        <Col span={16} style={{ fontSize: 25 }}>Категории</Col>
        {user.type === 'admin' && <Col span={8}><Button style={{ width: '100%' }} onClick={() => setIsModalOpen(true)}>Добавить <PlusOutlined /></Button></Col>}
        <Col span={24}>
          <Table 
            columns={columns} 
            dataSource={data} 
            rowKey='id' 
          />
        </Col>
      </Row>
      
      {isModalOpen && <Modal
        title={getModalTitle()}
        open={true}
        onCancel={() => {setFileList([]);closeModal()} }
        footer={[<Button loading={isLoading} onClick={handleSave}> Сохранить </Button>]}
      >
        <div style={{ marginBottom: 15 }}>
          <div style={{ marginBottom: 5 }}>Название категории:</div>
          <Input 
            value={currentCategory.title} 
            onChange={(e)=>setCurrentCategory({...currentCategory, title: e.target.value})}
            status={validationErrors.title ? 'error' : ''}
          />
          {validationErrors.title && (
            <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
              {validationErrors.title}
            </div>
          )}
        </div>
        <div style={{ marginBottom: 15 }}>
          <div style={{ marginBottom: 5 }}>Изображение:</div>
          <Uploader fileList={fileList} setFileList={setFileList}/>
          {validationErrors.image && (
            <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
              {validationErrors.image}
            </div>
          )}
        </div>
      </Modal>}

      <Modal
        title="Удаление категории"
        open={isDeleteModalOpen}
        onOk={confirmDelete}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setDeleteCategoryId(null);
        }}
        okText="Удалить"
        cancelText="Отмена"
      >
        <p>Вы уверены, что хотите удалить эту категорию?</p>
      </Modal>
    </Spin>
  );
};

export default Categories;
